import _ from 'lodash';
import React, {FunctionComponent, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {Paginator} from '@treez-ui/core/table';
import {
    generateQueryString,
    getProductQueryParameters,
    getSearchQueryParameters,
    parseQueryString,
} from '~/lib/routing';
import {CurrentSelectionProvider} from '~/lib/selectedProductContext';
import {HighlightedAndRegularProducts, ProductParameters} from '~/lib/sharedInterfaces';
import {BrowseBrandTreezPromptModal} from '~/modules/widgets/BrowseBrandTreezPromptModal';
import {generateBrandTreezRoute} from '~/Routes';
import {selectOrganizationId} from '~/redux/selectors/organizationSelectors';
import NoProductsFoundPrompt from './NoProductsFoundPrompt';
import OrganizationCatalogActions from './OrganizationCatalogActions';
import {OrganizationProductTable} from './OrganizationProductTable';
import StoreMatrixView from './StoreMatrixView';
import {useOrgCatalogState} from '~/contexts/OrgCatalog/orgCatalogContext';
import JumpToPage from '~/modules/widgets/JumpToPage';

interface Props {
    highlightedAndRegularProducts: HighlightedAndRegularProducts;
    productCount: number;
    openProductForm: () => void;
    hideStores: boolean;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

export const OrganizationProducts: FunctionComponent<Props> = (props: Props) => {
    const {productCount, highlightedAndRegularProducts, hideStores, openProductForm} = props;

    const location = useLocation();
    const history = useHistory();

    const {fetching} = useOrgCatalogState();

    const [browseBrandTreezPromptOpen, setBrowseBrandTreezPromptOpen] = useState<boolean>(false);

    const queryParams = parseQueryString(location.search);
    const productParams = getProductQueryParameters(queryParams);
    const searchParams = getSearchQueryParameters(queryParams);

    const {page, pageSize} = searchParams;

    const organizationId = useSelector(selectOrganizationId);

    const combinedProducts = useMemo(
        () => [
            ...highlightedAndRegularProducts.highlightedProducts,
            ...highlightedAndRegularProducts.otherProducts,
        ],
        [highlightedAndRegularProducts]
    );

    const noProductsFound = combinedProducts.length === 0 && fetching === false;

    const redirectToBrandTreez = (brandQueryParams?: ProductParameters) => {
        const {pathname} = generateBrandTreezRoute(organizationId);

        history.push({
            pathname,
            search: queryParams ? generateQueryString(brandQueryParams) : '',
        });
    };

    const handlePaginationChange = (page: number, pageSize: number) => {
        history.push({
            search: generateQueryString({
                ...queryParams,
                page: page + 1,
                pageSize,
            }),
        });
    };

    return (
        <CurrentSelectionProvider>
            {noProductsFound ? (
                <NoProductsFoundPrompt
                    openProductForm={openProductForm}
                    redirectToBrandTreez={() => redirectToBrandTreez(productParams)}
                />
            ) : (
                <div className="organization-products-container">
                    <div
                        className={`organization-products-table-exterior-container${
                            hideStores || fetching ? '' : '-with-stores'
                        }`}
                    >
                        <OrganizationProductTable
                            highlightedProducts={highlightedAndRegularProducts.highlightedProducts}
                            combinedProducts={combinedProducts}
                        />
                    </div>
                    <StoreMatrixView open={hideStores === false} products={combinedProducts} />
                </div>
            )}
            <div className="pagination-container">
                {noProductsFound === false && (
                    <>
                        <Paginator
                            numItems={productCount}
                            pageIdx={page ? page - 1 : DEFAULT_PAGE - 1}
                            pageSize={
                                _.isNumber(Number(pageSize)) ? Number(pageSize) : DEFAULT_PAGE_SIZE
                            }
                            onChange={handlePaginationChange}
                            sizeOptions={[10, 25]}
                        />
                        <JumpToPage
                            onChange={handlePaginationChange}
                            numItems={productCount}
                            pageSize={
                                _.isNumber(Number(pageSize)) ? Number(pageSize) : DEFAULT_PAGE_SIZE
                            }
                        />
                    </>
                )}
            </div>
            <OrganizationCatalogActions
                openBrowseBrandTreezPrompt={() => setBrowseBrandTreezPromptOpen(true)}
            />
            <BrowseBrandTreezPromptModal
                open={browseBrandTreezPromptOpen}
                openProductForm={() => {
                    setBrowseBrandTreezPromptOpen(false);
                    openProductForm();
                }}
                closePrompt={() => setBrowseBrandTreezPromptOpen(false)}
                redirectToBrandTreez={() => redirectToBrandTreez(productParams)}
            />
        </CurrentSelectionProvider>
    );
};

export default React.memo(OrganizationProducts);
